import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X, ArrowRight, Sparkles } from "lucide-react";

/**
 * OnboardingTour — a lightweight, dependency-free 4-step spotlight tour.
 * Reads `harmonizer_tour_seen` from localStorage; renders nothing once completed
 * (or when the user hits "Skip"). Steps target elements via data-testid; the
 * highlighted node is spotlit with a soft outline + a floating card next to it.
 *
 * The tour auto-triggers on the first render of ProjectList when the flag
 * is absent, and can be re-launched from Workspace via `?tour=1` or by the
 * caller passing `force`.
 */

const STORAGE_KEY = "harmonizer_tour_seen";

const STEPS = [
  {
    selector: '[data-testid="btn-new-project"]',
    title: "Create your first project",
    body: "Click here to spin up a new project. Start with the seeded demo (Project Nexus) or an empty template — you can rename, duplicate or delete at any time.",
    placement: "bottom",
  },
  {
    selector: '[data-testid="btn-csuite-portal"]',
    title: "Executive C-Suite portal",
    body: "CIOs and CFOs get a portfolio-level dashboard here: EBITDA snapshot, ROI by project, constraints, and a health histogram across every project you own.",
    placement: "bottom",
  },
  {
    selector: '[data-testid="btn-cover"]',
    title: "Home & branding",
    body: "The header logo returns you to the cover page. Every export you generate — PDF, Word, PPT — is co-branded with capittance.com.",
    placement: "bottom",
  },
  {
    selector: 'body',
    title: "You're all set",
    body: "Open a project and try the tabs across the top: Dashboard · Tracker · Requirements · Documents · Budget · RAID · Actions · Stakeholders · Governance · S/4HANA · and more. Everything autosaves as you type.",
    placement: "center",
  },
];

export default function OnboardingTour({ force = false, onDone }) {
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    if (force) { setActive(true); return; }
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setActive(true);
    } catch { /* localStorage disabled — skip tour silently */ }
  }, [force]);

  const step = STEPS[idx];

  useLayoutEffect(() => {
    if (!active || !step) return;
    const node = step.selector === 'body' ? null : document.querySelector(step.selector);
    if (!node) { setRect(null); return; }
    const r = node.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    // Also scroll into view (safe when target is off-screen)
    try { node.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" }); } catch { /* noop */ }
  }, [active, idx, step]);

  useEffect(() => {
    const onResize = () => {
      if (!step || step.selector === 'body') return;
      const node = document.querySelector(step.selector);
      if (!node) return;
      const r = node.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [step]);

  const finish = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
    setActive(false);
    onDone && onDone();
  };
  const next = () => (idx < STEPS.length - 1 ? setIdx(idx + 1) : finish());
  const skip = finish;

  if (!active) return null;

  // Card placement: default to bottom of the target; fall back to viewport center.
  const cardStyle = { position: "fixed", zIndex: 1001, maxWidth: 360 };
  if (rect && step.placement === "bottom") {
    cardStyle.top = Math.min(window.innerHeight - 260, rect.top + rect.height + 12);
    cardStyle.left = Math.max(16, Math.min(window.innerWidth - 380, rect.left));
  } else {
    cardStyle.top = "50%";
    cardStyle.left = "50%";
    cardStyle.transform = "translate(-50%, -50%)";
  }

  const ringStyle = rect ? {
    position: "fixed",
    zIndex: 1000,
    top: rect.top - 6,
    left: rect.left - 6,
    width: rect.width + 12,
    height: rect.height + 12,
    borderRadius: 12,
    boxShadow: "0 0 0 3px #0369A1, 0 0 0 9999px rgba(15, 23, 42, 0.55)",
    pointerEvents: "none",
    transition: "all 240ms ease",
  } : null;

  return (
    <div data-testid="onboarding-tour">
      {ringStyle ? (
        <div style={ringStyle} data-testid="onboarding-spotlight" />
      ) : (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15, 23, 42, 0.55)", pointerEvents: "none" }} />
      )}
      <div ref={cardRef} style={cardStyle} className="card p-5 shadow-xl border-2" data-testid={`onboarding-step-${idx}`}>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-6 h-6 rounded-full grid place-items-center" style={{ background: "#0369A1" }}>
            <Sparkles size={12} color="#fff" />
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            Step {idx + 1} of {STEPS.length}
          </div>
          <button
            onClick={skip}
            className="ml-auto text-zinc-400 hover:text-zinc-700"
            title="Skip tour"
            data-testid="onboarding-skip"
          >
            <X size={16} />
          </button>
        </div>
        <div className="font-display text-lg font-extrabold tracking-tight mb-1">{step.title}</div>
        <div className="text-sm text-zinc-600 leading-relaxed mb-4">{step.body}</div>
        <div className="flex items-center justify-between">
          <button className="text-xs text-zinc-500 underline" onClick={skip} data-testid="onboarding-skip-link">
            Skip tour
          </button>
          <button
            className="btn btn-primary btn-sm inline-flex items-center gap-1"
            onClick={next}
            data-testid="onboarding-next"
          >
            {idx < STEPS.length - 1 ? "Next" : "Got it"}
            <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
